import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { MultiSelectComboboxField, SelectOption } from '@/components/form-components/MultiSelectCombobox'
import { Badge } from '@/components/shadcn/badge'

import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconBg } from '@/components/ui/IconBg'
import { IconCommunityOutline } from '@icons/IconCommunityOutline'

type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']

// Validation schema
const formSchema = z.object({
  selectedTeamIds: z.array(z.number()).min(1, 'Please select at least one team to add'),
})

export type ManageProjectTeamsForm = z.infer<typeof formSchema>

// Minimal shape used by this form
type SelectedWorkspaceLite = {
  organisation_id: number
  workspaceId: number
  app_id: number
}

type ManageProjectTeamsFormV2Props = {
  selectedWorkspace: SelectedWorkspaceLite
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
}

export const ManageProjectTeamsFormV2 = (props: ManageProjectTeamsFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} />
  )
}

const FormComponent = (props: ManageProjectTeamsFormV2Props) => {
  const {
    getSubscriptionsApiGetOrganisationTeams,
    getWorkspacesApiGetProjectTeamsWorkspace,
    putWorkspacesApiAddProjectTeamToWorkspace,
    deleteWorkspacesApiDeleteProjectTeamFromWorkspace,
  } = useRestClient()

  // All project-scope teams (organisation-level)
  const {
    data: orgTeams,
    isPending: orgTeamsLoading,
    isError: orgTeamsError,
  } = getSubscriptionsApiGetOrganisationTeams.useQuery({ query: { orgId: props.selectedWorkspace.organisation_id } })

  // Current teams already on the workspace
  const {
    data: currentTeams,
    isPending: currentTeamsLoading,
    isError: currentTeamsError,
  } = getWorkspacesApiGetProjectTeamsWorkspace.useQuery({ query: { workspaceId: props.selectedWorkspace.workspaceId } })

  const invalidateProjectTeams = () => {
    getWorkspacesApiGetProjectTeamsWorkspace.invalidateQueries({
      parameters: { query: { workspaceId: props.selectedWorkspace.workspaceId } },
    })
  }

  const { mutateAsync: addProjectTeam } = putWorkspacesApiAddProjectTeamToWorkspace.useMutation(undefined, {
    onSuccess: () => {
      invalidateProjectTeams()
      toast.success('Teams successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error adding teams')
    },
  })

  const { mutate: deleteProjectTeam } = deleteWorkspacesApiDeleteProjectTeamFromWorkspace.useMutation(undefined, {
    onSuccess: () => {
      invalidateProjectTeams()
      toast.success('Team successfully removed')
    },
    onError: () => {
      toast.error('Error removing team')
    },
  })

  const defaultValues = useMemo(
    () => ({
      selectedTeamIds: [] as number[],
    }),
    []
  )

  const form = useForm<ManageProjectTeamsForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  // Determine all available teams and filter out ones already in workspace; ensure correct application match
  const availableTeams = useMemo(() => {
    const all = orgTeams ?? []
    const currentIds = new Set((currentTeams ?? []).map((t) => t.id))
    return all.filter((t) => t.applicationId == props.selectedWorkspace.app_id).filter((t) => !currentIds.has(t.id))
  }, [orgTeams, currentTeams, props.selectedWorkspace.app_id])

  // Options for combobox with disambiguation for duplicate names
  const teamOptions: SelectOption<number>[] = useMemo(() => {
    const sorted = [...availableTeams].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    const nameCounts: Record<string, number> = {}
    sorted.forEach((t) => {
      const key = t.name || 'Unnamed Team'
      nameCounts[key] = (nameCounts[key] || 0) + 1
    })
    return sorted.map((t) => {
      const base = t.name || 'Unnamed Team'
      const label = nameCounts[base] > 1 ? `${base} (ID: ${t.id})` : base
      return { value: t.id, label, description: t.description }
    })
  }, [availableTeams])

  const handleSubmit = async (data: ManageProjectTeamsForm) => {
    await Promise.all(
      data.selectedTeamIds.map((teamId) =>
        addProjectTeam({ body: { subscriptionId: props.selectedWorkspace.workspaceId, teamId } })
      )
    )
    form.reset()
  }

  const handleDeleteTeam = (teamId: number) => {
    deleteProjectTeam({ body: { subscriptionId: props.selectedWorkspace.workspaceId, teamId } })
  }

  const isLoading = orgTeamsLoading || currentTeamsLoading
  const isError = orgTeamsError || currentTeamsError
  if (isLoading) return <FormComponentLoading />
  if (isError) return <ErrorComponent />

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <MultiSelectComboboxField
                control={form.control}
                name="selectedTeamIds"
                label="Add Project Teams"
                options={teamOptions}
                placeholder="Select project teams to add..."
                searchPlaceholder="Search teams..."
                emptyMessage="No available teams found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit" disabled={teamOptions.length === 0}>
                Add Selected Teams
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {(currentTeams ?? []).length > 0 && (
        <div className="space-y-4">
          <div className="text-sm font-medium">Current Teams:</div>
          <div className="space-y-2">
            {(currentTeams ?? []).map((team) => (
              <TeamCard key={team.id} team={team} onDelete={() => handleDeleteTeam(team.id)} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

const TeamCard = ({ team, onDelete }: { team: MessagesSubscriptionsOrganisationTeam; onDelete: () => void }) => {
  const name = team.name || 'Unnamed Team'
  return (
    <div className="flex items-center justify-between rounded-lg border bg-background p-3">
      <div className="flex items-center gap-5">
        <IconBg tooltip="Project team" className="size-12 bg-primary/30">
          <IconCommunityOutline className="size-5 text-muted-foreground" />
        </IconBg>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{name}</span>
          <Badge variant="outline">{team.id}</Badge>
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <IconDeleteOutline />
      </Button>
    </div>
  )
}
